import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import "./Drawer.css";
import { FontAwesomeIcons } from "../../core/Types";
import IconButton from "../base/IconButton";
import FlexRow from "../base/FlexRow";
import { addComponentState, removeComponentState, useScreen } from "../../core/Global";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { usePWAStatus } from "../../hooks/usePWAStatus";
import { withAlpha } from "../../core/Helper";

export type DrawerRef = {
    close: () => void;
};

// --- Drawer stacking ---------------------------------------------------------
// Each opened drawer gets an increasing z-index so a drawer opened on top of
// another sits above it AND above the previous drawer's overlay. KTDrawer sets
// each drawer's own overlay to (panel z-index - 1), so clicking the topmost
// overlay closes only the topmost drawer.
const DRAWER_BASE_Z = 110;   // matches the .drawer CSS z-index so base layering is unchanged
const DRAWER_Z_STEP = 20;
const openDrawerIds: string[] = [];

// --- Desktop "paper stack" effect ---------------------------------------------
// When a drawer opens on top of another, every drawer underneath is pushed left
// (inline translateX) so a strip of it stays visible beside the new drawer, like
// sheets of paper laid on top of each other. Closing the top drawer slides the
// ones below back to their place. The .drawer CSS already has
// `transition: transform .3s !important`, so simply changing the inline
// transform animates in sync with KTDrawer's own open/close animation.
// IMPORTANT: `.drawer.drawer-end { transform: translateX(100%) }` (the closed
// state) is NOT !important, so any leftover inline transform would break the
// close animation — releaseStack() must always clear it.
const STACK_PEEK_PX = 48;

// Drawers that opted into `stackReveal`: when covered by another drawer they
// slide left until most of their width (STACK_REVEAL_RATIO) shows beside it
// (the user still needs their content — e.g. the job quick view). The rest
// stays tucked under the new drawer so the stack still reads as layered sheets.
const STACK_REVEAL_RATIO = 0.85;
const stackRevealDrawerIds = new Set<string>();

// How far (px) a drawer is shifted LEFT by its own open side panel (via the
// `right` style). The drawers underneath must follow this shift so they stay
// tucked behind the pushed drawer instead of poking out from under the panel.
const sidePanelPushById = new Map<string, number>();

const parsePushPx = (w?: string): number => {
    if (!w) return Math.round(window.innerWidth * 0.33);
    if (w.endsWith("px")) return parseFloat(w) || 0;
    if (w.endsWith("vw")) return Math.round((parseFloat(w) || 0) / 100 * window.innerWidth);
    const n = parseFloat(w);
    return isNaN(n) ? Math.round(window.innerWidth * 0.33) : n;
};

const updateStackOffsets = () => {
    // Mobile drawers are full-width; keep the plain stacked behavior there.
    const stackingEnabled = window.innerWidth >= 992;

    let leftEdgeAbove = Number.POSITIVE_INFINITY;
    for (let i = openDrawerIds.length - 1; i >= 0; i--) {
        const did = openDrawerIds[i];
        const el = document.querySelector<HTMLElement>("#" + did);
        if (!el) continue;

        // Extra left shift this drawer gets from its own open side panel.
        const push = sidePanelPushById.get(did) ?? 0;
        const isTop = i === openDrawerIds.length - 1;
        const naturalLeft = window.innerWidth - el.offsetWidth;

        if (isTop || !stackingEnabled) {
            el.style.transform = "";
            // Its real left edge is shifted by `push` (applied via `right`).
            leftEdgeAbove = naturalLeft - push;
            continue;
        }

        // Push this drawer left until the wanted part of it shows beside the
        // drawer above: its full width for stackReveal drawers, otherwise a
        // peek strip. Because leftEdgeAbove already accounts for the panel push,
        // lower drawers automatically follow a pushed drawer above them.
        // Clamped so it never goes past the screen's left edge, and never pushed
        // right.
        const visibleWidth = stackRevealDrawerIds.has(did) ? el.offsetWidth * STACK_REVEAL_RATIO : STACK_PEEK_PX;
        const wanted = Math.max(0, leftEdgeAbove - visibleWidth);

        // ...and always by at least the peek, even when enough of it already
        // shows. A drawer below that happens to be wider than the one covering
        // it satisfies "a strip is visible" without moving at all, and then
        // nothing about the screen says a second drawer just opened over the
        // first -- it reads as one wide panel with a narrow one glued on. The
        // movement is what makes them read as sheets.
        const desiredLeft = Math.min(wanted, naturalLeft - STACK_PEEK_PX);
        const offset = Math.min(0, Math.max(-naturalLeft, desiredLeft - naturalLeft));
        el.style.transform = offset < 0 ? `translateX(${offset}px)` : "";
        leftEdgeAbove = naturalLeft + offset - push;
    }
};

window.addEventListener("resize", () => {
    if (openDrawerIds.length > 1) updateStackOffsets();
});

export type DrawerProps = {
    id?: string
    /** Usually words; a drawer about one person may put their picture beside the name. */
    title?: React.ReactNode
    titleIcon?: FontAwesomeIcons
    titleComponent?: any
    // Renders titleComponent on its own full-width row under the title instead of inline beside
    // it — for content that needs the room, like a job's flag chips.
    titleComponentBelow?: boolean
    subtitle?: string
    toolbarComponent?: any
    children?: React.ReactNode
    noPadding?: boolean
    noScroll?: boolean
    noHeader?: boolean
    // With titleComponentBelow, keeps the toolbar OUT of the title's row so the header centres it
    // against its full height -- title row and the row below it together -- instead of lining it
    // up with the title alone. For a wizard whose steps are that second row, buttons level with
    // the title read as belonging to the title rather than to the header.
    toolbarCenter?: boolean
    // Drops the header's own close button. For a drawer whose toolbar already carries the way
    // out -- a wizard's Cancel, say -- where a second control that also closes it is one too
    // many. The mobile header keeps its back chevron: it is the only way back there.
    hideClose?: boolean
    // When true, the whole drawer (full-height panel + header + body) shares the
    // iOS grouped background so the header blends into the body instead of being
    // a separate white bar. Header border is dropped too.
    groupedBg?: boolean
    preventSwip?: boolean
    width?: any
    focusOn?: string
    permanent?: boolean
    size?: "normal" | "medium" | "large" | "xlarge"
    // An extra full-height layer that opens OUTSIDE the drawer, attached to its
    // left edge (e.g. a map). It does NOT shrink the drawer. When open, the
    // drawer's own close button moves onto this panel (top-left). Desktop only.
    sidePanel?: React.ReactNode
    sidePanelOpen?: boolean
    sidePanelWidth?: string
    // Paper-stack behavior when another drawer opens on top of this one:
    // by default only a small strip stays visible; with stackReveal the whole
    // drawer slides left so its full content remains readable beside the new one.
    stackReveal?: boolean
    // Optional accent colour for the drawer's identity (e.g. a job's colour): a soft tint of it
    // fades down from the very top, through the header and into the content below it.
    accentColor?: string
    // Optional bar pinned to the drawer's top edge, above the header. Rendered whether or not
    // accentColor is set, so it can double as the control that PICKS the accent colour.
    accentComponent?: any
    onOpen?: () => void
    onClose?: () => void
}
export const Drawer = React.forwardRef<DrawerRef, DrawerProps>(function Drawer(props, ref) {

    const { isIOS, isAndroid, isStandalone } = usePWAStatus();

    const [opened, openedSet] = useState<boolean>(false)
    const container = useMemo(() => document.createElement("div"), []);
    useEffect(() => {
        document.body.appendChild(container);
        return () => {

            if (container.parentElement) container.parentElement.removeChild(container);
        };
    }, [container]);

    const navigate = useNavigate()
    const screen = useScreen()
    const KTDrawer = window.GetKTDrawerInstance();

    const id = useMemo(
        () => props.id ?? `drawer-${Math.random().toString(36).slice(2)}`,
        [props.id]
    );

    useImperativeHandle(ref, () => ({
        close: CloseDrawer,
    }), []);

    const releaseStack = () => {
        const i = openDrawerIds.indexOf(id);
        if (i > -1) {
            openDrawerIds.splice(i, 1);
            stackRevealDrawerIds.delete(id);
            sidePanelPushById.delete(id);
            // Clear our inline transform so the CSS close animation owns the
            // element again (see the paper-stack note above), then slide the
            // remaining drawers back toward their natural place.
            const el = document.querySelector<HTMLElement>("#" + id);
            if (el) el.style.transform = "";
            updateStackOffsets();
        }
        if (openDrawerIds.length === 0) document.body.style.overflow = 'auto';
    };

    useEffect(() => {
        if (id && KTDrawer) {
            KTDrawer.createInstances();
            var drawerElement = document.querySelector("#" + id);
            var drawer = KTDrawer.getInstance(drawerElement);

            drawer.on("kt.drawer.shown", function () {
                setTimeout(() => {
                    if (props.focusOn) $("#" + props.focusOn).focus();
                    props.onOpen?.();
                }, 100);
                drawer.off("kt.drawer.shown");
            });

            drawer.on("kt.drawer.hide", function () {
                // Fires as the close animation STARTS, so the drawers underneath
                // slide back to their place in sync with this one sliding out.
                drawer.off("kt.drawer.hide");
                releaseStack();
            });

            drawer.on("kt.drawer.after.hidden", function () {
                setTimeout(() => {
                    props.onClose?.();
                    //window.allowMauiAppClose = true;
                }, 200);
                drawer.off("kt.drawer.after.hidden");
                releaseStack();
            });

            // Assign a stacked z-index BEFORE show() so KTDrawer's overlay
            // (panel z-index - 1) lands above the previous drawer.
            if (openDrawerIds.indexOf(id) < 0) openDrawerIds.push(id);
            if (props.stackReveal) stackRevealDrawerIds.add(id);
            const z = DRAWER_BASE_Z + (openDrawerIds.indexOf(id)) * DRAWER_Z_STEP;
            if (drawerElement) (drawerElement as HTMLElement).style.zIndex = String(z);

            drawer.show();
            // Push the drawers underneath to the left (paper-stack effect);
            // their transition runs in sync with this drawer's slide-in.
            updateStackOffsets();
            document.body.style.overflow = 'hidden';
            openedSet(true)
        }
    }, [id])

    const CloseDrawer = () => {
        var drawerElement = document.querySelector("#" + id);
        var drawer = KTDrawer.getInstance(drawerElement);
        drawer.hide();
    }


    const startXRef = useRef<number | null>(null);
    const startYRef = useRef<number | null>(null);
    const lockedRef = useRef(false); // optional: axis lock helper

    const SWIPE_EDGE_PX = 30; // swipe only activates within this distance from the left edge

    const onTouchStart: React.TouchEventHandler<HTMLDivElement> = (e) => {
        if (isStandalone && isIOS) {

            const target = e.target as Element | null;
            if (target && target.id == "map-job-for-tech") {
                startXRef.current = null;
                startYRef.current = null;
                lockedRef.current = false;
                return;
            }

            // if touch starts inside a "no-swipe" zone (e.g. signature canvas), disable swipe
            if (target && target.closest('[data-drawer-no-swipe="true"]')) {
                startXRef.current = null;
                startYRef.current = null;
                lockedRef.current = false;
                return;
            }

            const t = e.touches[0];

            // only activate swipe when the touch starts near the left edge of the screen
            if (t.clientX > SWIPE_EDGE_PX) {
                startXRef.current = null;
                startYRef.current = null;
                lockedRef.current = false;
                return;
            }

            // record initial touch point
            startXRef.current = t.clientX;
            startYRef.current = t.clientY;

            // reset axis lock for this gesture
            lockedRef.current = false;
        }

    };

    const onTouchMove: React.TouchEventHandler<HTMLDivElement> = (e) => {

        if (isStandalone && isIOS) {

            const target = e.target as Element | null;
            if (target && target.id == "map-job-for-tech") {
                return;
            }

            // Ignore if we don't have a starting point
            if (startXRef.current == null) return;

            const x = e.touches[0].clientX;
            const dx = x - startXRef.current; // >0 means swipe right

            // Optional: prevent default once we decide it's a horizontal gesture
            //if (dx > 0) e.preventDefault(); // stops back-swipe helpers on some browsers

            // If user swiped right beyond threshold → close the drawer
            if (dx > 100) {
                CloseDrawer()            // your close function
                startXRef.current = null; // reset so it won't fire repeatedly
            }
        }
    };


    useEffect(() => {

        if (isStandalone && isAndroid) {
            window.history.pushState({}, '')

            window.addEventListener('popstate', CloseDrawer);
            return () => {
                window.removeEventListener('popstate', CloseDrawer);
            };
        }

    }, [])


    // Drawer widths are managed here for every size. Each size scales with the
    // viewport (so smaller screens still get a roomy ~half-screen drawer) but is
    // capped in px so large/4K screens don't end up with an absurdly wide drawer.
    // Below the `lg` breakpoint the data attribute forces 100% (see below).
    const width = () => {
        if (props.width) {
            return `${props.width}`;
        }

        const vw = screen.width;
        if (props.size == "xlarge") {
            return `${Math.min(vw * 0.9, 1400)}px`;
        }
        else if (props.size == "large") {
            return `${Math.min(vw * 0.5, 820)}px`;
        }
        else if (props.size == "medium") {
            return `${Math.min(vw * 0.42, 680)}px`;
        }

        // normal / default
        return `${Math.min(vw / 3, 520)}px`;
    }

    // Allow the size/width to change WHILE the drawer is open and animate it.
    // KTDrawer sets the panel width inline on show, so when the prop changes we
    // re-apply it directly; the width transition (style.extend.css) animates it.
    useEffect(() => {
        if (!opened) return;
        if (screen.width < 992) return; // below lg the drawer is full width
        const el = document.querySelector<HTMLElement>("#" + id);
        if (!el) return;
        el.style.width = width();
        updateStackOffsets();
    }, [props.size, props.width, opened])

    // Track this drawer's side-panel push so the drawers underneath follow it
    // (they animate their transform in sync with this drawer's `right` shift).
    useEffect(() => {
        if (!opened) return;
        const pushPx = (props.sidePanel && props.sidePanelOpen && screen.width >= 992)
            ? parsePushPx(props.sidePanelWidth)
            : 0;
        sidePanelPushById.set(id, pushPx);
        updateStackOffsets();
    }, [props.sidePanel, props.sidePanelOpen, opened])


    useEffect(() => {

        addComponentState({
            id,
            close: CloseDrawer
        })

        return () => {
            removeComponentState(id);
            releaseStack();
        }

    }, [])


    return createPortal(
        <>
            <div id={id} className={props.groupedBg ? "drawer-grouped-bg" : "bg-body"} data-kt-drawer="true" style={{
                overflow: props.sidePanel ? "visible" : "hidden",
                // Opening the side panel pushes the drawer left by the panel width
                // so the panel (anchored to the drawer's right edge) slides into
                // view from the screen's right edge. Keep `right` an explicit px
                // value (0px <-> Wpx) so the transition actually animates.
                right: (props.sidePanel && !screen.isMobile)
                    ? (props.sidePanelOpen ? (props.sidePanelWidth ?? "33vw") : "0px")
                    : undefined,
            }}
                data-kt-drawer-name="default" data-kt-drawer-activate="true" data-kt-drawer-overlay="true"
                data-kt-drawer-width={`{default:'100%', 'lg': '${width()}', 'xl': '${width()}'}`}
                data-kt-drawer-direction="end" data-kt-drawer-permanent={(props.permanent ? "true" : "false")}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
            >

                {
                    // Full-height layer attached to the drawer's RIGHT edge (outside it),
                    // so it fills the gap to the screen's right edge once the drawer is
                    // pushed left. It's a child of the drawer, so it shares the drawer's
                    // stacking context and sits above the overlay automatically.
                    props.sidePanel && !screen.isMobile &&
                    <div style={{
                        position: "absolute",
                        top: 0,
                        bottom: 0,
                        left: "100%",
                        width: props.sidePanelWidth ?? "33vw",
                        overflow: "hidden",
                        backgroundColor: "var(--bs-body-bg)",
                        boxShadow: props.sidePanelOpen ? "-10px 0 24px rgba(0,0,0,0.12)" : "none",
                    }}>
                        {props.sidePanel}
                    </div>
                }


                <div className="card shadow-none rounded-0 w-100"
                    style={{
                        minHeight: 60,
                        position: "relative",
                        // The tint sits on the CARD, not the header, so it bleeds past the header's
                        // bottom border and fades out somewhere inside the content below it (the
                        // tabs). Both .card-header and .card-body are transparent, so it shows
                        // through both.
                        ...(props.accentColor ? {
                            backgroundImage: `linear-gradient(180deg, ${withAlpha(props.accentColor, .20)} 0%, transparent 100%)`,
                            backgroundRepeat: "no-repeat",
                            backgroundSize: "100% 220px",
                        } : {})
                    }}>

                    {props.accentComponent && <props.accentComponent />}

                    {
                        props.noHeader ? <></>
                            :
                            <div id="kt_drawer_job_header" className="card-header py-3 px-5"
                                style={{
                                    // .card-header:first-child rounds its top corners from
                                    // --bs-card-inner-border-radius, which `rounded-0` on the card
                                    // does NOT reset — that's what curves the drawer's top edge.
                                    borderRadius: 0,
                                    // .drawer stretches the card to a definite height and the card
                                    // is a flex column, so content taller than the drawer makes
                                    // flexbox SHRINK the header (down to its 70px min) — by an
                                    // amount that depends on what the open tab renders. That moved
                                    // the header's own rows around and spilled them over the
                                    // content below. Only the body may absorb the overflow.
                                    flexShrink: 0,
                                }}>

                                <div className={(props.titleComponentBelow && !props.toolbarCenter) ? "w-100" : undefined}
                                    style={(props.titleComponentBelow && props.toolbarCenter)
                                        // Takes what the toolbar beside it does not, rather than
                                        // the whole width, which would leave the toolbar nowhere
                                        // to sit.
                                        ? { flex: "1 1 auto", minWidth: 0 }
                                        : undefined}>

                                    {/* Title and the action buttons share one row, so the buttons stay
                                        level with the title however many rows follow underneath. */}
                                    <FlexRow style={{
                                        // The 60px floor exists to give a bare title row some
                                        // presence. With a row underneath it only adds dead space
                                        // between the title and that row.
                                        minHeight: ((props.subtitle && !screen.isMobile) || props.titleComponentBelow) ? undefined : 60
                                    }}>
                                        {
                                            screen.isMobile ?
                                                <FlexRow>
                                                    <IconButton className="drawer-mobile-back" size={16} gap={8} icon="chevron-left" type="thin" onClick={CloseDrawer} />
                                                    <div className="drawer-mobile-title">
                                                        <div className="drawer-mobile-title-text">
                                                            {props.title}
                                                        </div>
                                                        {
                                                            props.subtitle &&
                                                            <div className="drawer-mobile-subtitle">
                                                                {props.subtitle}
                                                            </div>
                                                        }
                                                    </div>
                                                </FlexRow>
                                                :
                                                <FlexRow >
                                                    <div className="fs-1 fs-xxxl-1 modal-title">
                                                        {props.title}
                                                    </div>
                                                </FlexRow>
                                        }

                                        {props.titleComponent && !props.titleComponentBelow && <props.titleComponent />}

                                        {
                                            props.titleComponentBelow && !props.toolbarCenter &&
                                            <div className="card-toolbar m-0 ms-auto">
                                                {props.toolbarComponent && <props.toolbarComponent />}
                                                {
                                                    !screen.isMobile && !props.hideClose &&
                                                    <IconButton id={`${id}-close`} size={26} gap={14}
                                                        icon="times" title="Close" type="light" onClick={CloseDrawer} />
                                                }
                                            </div>
                                        }
                                    </FlexRow>
                                    {
                                        props.subtitle && !screen.isMobile &&
                                        <div className="fs-7 mt-1">
                                            {props.subtitle}
                                        </div>
                                    }
                                    {props.titleComponent && props.titleComponentBelow && <props.titleComponent />}
                                </div>




                                {
                                    // A direct child of the header, which centres its children --
                                    // so the toolbar sits level with the middle of the whole
                                    // header rather than with the title row inside it.
                                    (!props.titleComponentBelow || props.toolbarCenter) &&
                                    <div className="card-toolbar m-0">

                                        {props.toolbarComponent && <props.toolbarComponent />}

                                        {
                                            !screen.isMobile && !props.hideClose &&
                                            <IconButton id={`${id}-close`} size={26} gap={14}
                                                icon="times" title="Close" type="light" onClick={CloseDrawer} />
                                        }

                                    </div>
                                }

                            </div>
                    }


                    {/* Plain `<id>-body`. It was `"#<id>-body`, quote and hash included, which is
                        not a selectable id: querySelector throws on it, and nothing could ever
                        have been looking the element up by it. */}
                    <div id={`${id}-body`} className={`card-body position-relative ${props.noScroll ? "" : "scroll-y"} ${props.noPadding ? "p-0" : ""}`}>

                        {props.children}

                    </div>
                </div>

            </div>

        </>
        , container)
})